import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/shared/ui/components/button';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { Forbidden } from '@/modules/auth/Forbidden';
import { useRoles, type Role } from './api';
import { CreateRoleDialog } from './CreateRoleDialog';
import { RoleDetailDialog } from './RoleDetailDialog';

export function RolesPage() {
  const { t } = useTranslation('common');
  const canManage = useAuthorization().can('iam.role.manage');

  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<Role | null>(null);

  const { data, isLoading, isError } = useRoles();

  if (!canManage) return <Forbidden />;

  return (
    <section className="grid gap-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('roles.title')}</h1>
        <Button onClick={() => setCreating(true)}>{t('roles.actions.create')}</Button>
      </div>

      {isLoading && <p className="text-muted-foreground">{t('roles.loading')}</p>}
      {isError && <p className="text-destructive">{t('roles.errors.loadFailed')}</p>}

      {data && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">{t('roles.columns.name')}</th>
                <th className="px-4 py-2 font-medium">{t('roles.columns.type')}</th>
                <th className="px-4 py-2 font-medium">{t('roles.columns.permissions')}</th>
                <th className="px-4 py-2 font-medium">{t('roles.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">
                    {t('roles.empty')}
                  </td>
                </tr>
              )}
              {data.items.map((role) => (
                <tr key={role.id} className="border-b last:border-0">
                  <td className="px-4 py-2">{role.name}</td>
                  <td className="px-4 py-2">
                    <span className={role.isSystem ? 'text-muted-foreground' : 'text-primary'}>
                      {role.isSystem ? t('roles.type.system') : t('roles.type.custom')}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{role.permissions.length}</td>
                  <td className="px-4 py-2">
                    <Button size="sm" variant="outline" onClick={() => setSelected(role)}>
                      {role.isSystem ? t('roles.actions.view') : t('roles.actions.manage')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateRoleDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={() => toast.success(t('roles.toast.created'))}
      />
      <RoleDetailDialog
        role={selected}
        onOpenChange={(open) => !open && setSelected(null)}
        onActioned={(message) => toast.success(message)}
      />
    </section>
  );
}
